import type { Chamado, ChamadoLocal } from "../types";

/**
 * Quantas respostas ao cliente ainda não foram vistas (DEV-107). Puro, para ser testável:
 * `widget.ts` não tem cobertura e o shadow é `mode: "closed"`.
 *
 * O total vem SEMPRE do servidor e o visto do storage local. É por isso que a conta é uma
 * subtração e não um contador incrementado no cliente: quando o time exclui um
 * comentário, o total diminui, e um contador que só soubesse somar deixaria a badge acesa
 * para sempre. Por isso também o `Math.max(0, …)` — visto pode passar o total.
 */
export function naoVistas(locais: ChamadoLocal[], remotos: Chamado[]): number {
  const vistoPorCodigo = new Map(locais.map((l) => [l.codigo, l.respostasVistas]));

  return remotos.reduce((total, c) => {
    const visto = vistoPorCodigo.get(c.codigo);
    // Chamado que o servidor conhece mas o storage não: nada a comparar. Acontece no
    // caminho logado (F4), onde a lista vem por identidade e não por token local.
    if (visto === undefined) return total;
    return total + Math.max(0, c.respostas - visto);
  }, 0);
}

/** Respostas não vistas de UM chamado — o marcador por linha da lista. */
export function naoVistasDo(local: ChamadoLocal | undefined, remoto: Chamado): number {
  if (!local) return 0;
  return Math.max(0, remoto.respostas - local.respostasVistas);
}

/**
 * Estado do launcher depois de uma sincronização (DEV-176): ele volta a aparecer quando
 * chega resposta nova, mesmo escondido.
 *
 * Com o botão oculto — pela flag do host ou pelo "Esconder" — a badge não tinha onde
 * aparecer: a resposta do time chegava e ninguém ficava sabendo. Era limitação declarada,
 * e o custo dela era o pior possível numa tela de acompanhamento: silêncio.
 *
 * A conta é contra uma LINHA DE BASE, não contra zero, e é isso que separa "esconder" de
 * "silenciar para sempre": quem esconde com respostas pendentes carimba o total daquele
 * momento, e o botão só volta quando chega algo ALÉM dele. Sem a base, o tick seguinte do
 * poller desfaria o esconder na cara da pessoa, e a única saída seria ler tudo.
 *
 * A base também DESCE junto com o total, e isso não é detalhe: quem escondeu devendo duas
 * respostas e depois as leu no painel voltaria a ter base 2 contra total 0 — a próxima
 * resposta (1) não passaria de 2 e o botão ficaria mudo para sempre. Descer é o que faz
 * "ler" quitar a dívida. (Descer também cobre o total diminuindo por exclusão do time.)
 *
 * Vale inclusive com `data-altbug-hotkey`, em que o botão nasce oculto por decisão do site:
 * a base nasce em 0 a cada load, então resposta pendente traz o launcher no primeiro tick.
 * É a escolha do produto — notificação que não aparece não é notificação.
 */
export function estadoDoLauncher(
  visivel: boolean,
  novas: number,
  baseAnterior: number,
): { visivel: boolean; base: number } {
  const base = Math.min(baseAnterior, novas);

  return { visivel: visivel || novas > base, base };
}
